import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { articles } from "@/mocks/articles";
import ArticleCard from "@/components/feature/ArticleCard";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import BackToTop from "@/components/feature/BackToTop";
import { useSeo } from "@/hooks/useSeo";

function useProgress() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const total = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(total > 0 ? Math.min(100, (window.scrollY / total) * 100) : 0);
    };
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return progress;
}

export default function ArticleDetail() {
  const { id } = useParams();
  const article = articles.find((item) => item.id === id);
  const progress = useProgress();

  useSeo({
    title: article
      ? `${article.title} - 看料大瓜现场直击`
      : "内容未找到 - 看料大瓜",
    description: article
      ? article.excerpt
      : "该内容可能已被移动或删除，请返回看料大瓜首页浏览最新现场直击。",
    keywords: article
      ? [...article.tags, "看料大瓜现场", "现场直击"].join(",")
      : "看料大瓜,现场直击",
    canonical: article
      ? `https://ahgslo.cn/article/${article.id}`
      : "https://ahgslo.cn/live",
    type: "article",
    jsonLd: article
      ? [
          {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: article.title,
            description: article.excerpt,
            image: article.cover,
            datePublished: article.publishDate,
            author: { "@type": "Organization", name: "看料大瓜" },
            publisher: { "@type": "Organization", name: "看料大瓜" },
            articleSection: article.categoryName,
            keywords: article.tags.join(","),
            inLanguage: "zh-CN",
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://ahgslo.cn/" },
              { "@type": "ListItem", position: 2, name: "现场直击", item: "https://ahgslo.cn/live" },
              { "@type": "ListItem", position: 3, name: article.title },
            ],
          },
        ]
      : undefined,
  });

  if (!article) {
    return (
      <main className="w-full">
        <div className="mx-auto flex max-w-[1240px] flex-col items-center px-6 py-24 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-400">
            <i className="ri-emotion-sad-line" />
          </span>
          <h1 className="mt-6 font-heading text-2xl font-black text-foreground-950">内容未找到</h1>
          <p className="mt-3 text-sm text-foreground-600">
            这篇内容可能已被移动或删除，不如回到现场首页看看最新的直击。
          </p>
          <Link
            to="/live"
            className="mt-6 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            返回现场直击
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </main>
    );
  }

  const related = articles
    .filter((item) => item.id !== article.id && item.category === article.category)
    .concat(articles.filter((item) => item.id !== article.id && item.category !== article.category))
    .slice(0, 3);

  return (
    <main className="w-full">
      <div className="fixed left-0 top-0 z-[60] h-0.5 w-full bg-transparent">
        <div className="h-full bg-primary-500 transition-[width] duration-150" style={{ width: `${progress}%` }} />
      </div>

      <div className="mx-auto max-w-[1240px] px-4 pt-6 md:px-6">
        <Breadcrumbs
          items={[
            { label: "现场直击", to: "/live" },
            { label: article.categoryName, to: `/live?cat=${article.category}` },
            { label: article.title },
          ]}
        />
      </div>

      <article className="w-full">
        <header className="mx-auto max-w-[820px] px-4 pt-8 md:px-6 md:pt-10">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-primary-100 px-3 py-1 text-[11px] font-medium text-primary-700">
              {article.categoryName}
            </span>
            {article.hot && (
              <span className="flex items-center gap-1 rounded-full bg-accent-100 px-3 py-1 text-[11px] text-accent-900">
                <i className="ri-fire-line" />
                热议现场
              </span>
            )}
          </div>
          <h1 className="mt-4 font-heading text-2xl font-black leading-tight text-foreground-950 md:text-4xl">
            {article.title}
          </h1>
          <p className="mt-4 text-base leading-relaxed text-foreground-600">{article.excerpt}</p>
          <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 border-y border-background-200 py-4 text-xs text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-3-line" />
              {article.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line" />
              {article.publishDate}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line" />
              阅读约 {article.readTime}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-eye-line" />
              {article.views.toLocaleString("zh-CN")} 阅读
            </span>
          </div>
        </header>

        <div className="mx-auto max-w-[920px] px-4 pt-8 md:px-6">
          <div className="h-[260px] w-full overflow-hidden rounded-lg md:h-[440px]">
            <img
              src={article.cover}
              alt={`${article.title} 现场图片`}
              title={`${article.title} - 看料大瓜现场直击`}
              className="h-full w-full object-cover object-top"
            />
          </div>
        </div>

        <div className="mx-auto grid max-w-[1240px] grid-cols-1 gap-10 px-4 py-10 md:px-6 md:py-14 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <div className="space-y-5 text-base leading-[1.9] text-foreground-800">
              {article.content.map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>

            {article.timeline && article.timeline.length > 0 && (
              <section className="mt-10 rounded-lg border border-background-200 bg-background-100 p-6">
                <h2 className="flex items-center gap-2 font-heading text-lg font-black text-foreground-950">
                  <i className="ri-timeline-view text-primary-600" />
                  事件时间线
                </h2>
                <ol className="mt-6 space-y-5">
                  {article.timeline.map((item, index) => (
                    <li key={item.time} className="relative flex gap-4 pl-1">
                      <span className="flex flex-col items-center">
                        <span
                          className={`flex h-3 w-3 rounded-full ${
                            index === 0 ? "bg-primary-500" : "bg-background-400"
                          }`}
                        />
                        {index !== article.timeline!.length - 1 && (
                          <span className="mt-1 w-px flex-1 bg-background-300" />
                        )}
                      </span>
                      <span className="pb-1">
                        <span className="font-heading text-sm font-black text-primary-700">
                          {item.time}
                        </span>
                        <span className="mt-1 block text-sm leading-relaxed text-foreground-700">
                          {item.text}
                        </span>
                      </span>
                    </li>
                  ))}
                </ol>
              </section>
            )}

            <div className="mt-10 flex flex-wrap items-center gap-2">
              <span className="text-xs text-foreground-500">相关标签</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full border border-background-300 bg-background-50 px-3 py-1 text-xs text-foreground-700"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <div className="mt-8 rounded-lg border border-accent-300 bg-accent-50 p-5">
              <p className="flex items-start gap-2 text-sm leading-relaxed text-foreground-700">
                <i className="ri-information-line mt-0.5 text-accent-700" />
                本文内容由编辑团队依据公开信息与现场描述整理。如信息有出入，欢迎通过页脚邮箱反馈，我们会核实后及时更正。
              </p>
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="sticky top-28 flex flex-col gap-5">
              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <h2 className="font-heading text-base font-black text-foreground-950">继续吃瓜</h2>
                <div className="mt-4 flex flex-col gap-3">
                  {related.map((item) => (
                    <Link
                      key={item.id}
                      to={`/article/${item.id}`}
                      className="group flex cursor-pointer gap-3"
                    >
                      <span className="h-16 w-20 shrink-0 overflow-hidden rounded-md">
                        <img
                          src={item.cover}
                          alt={`${item.title} 现场图片`}
                          title={`${item.title} - 看料大瓜现场直击`}
                          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                      </span>
                      <span className="min-w-0">
                        <span className="line-clamp-2 text-sm font-medium text-foreground-900 transition-colors group-hover:text-primary-700">
                          {item.title}
                        </span>
                        <span className="mt-1 block text-[11px] text-foreground-500">
                          {item.categoryName}
                        </span>
                      </span>
                    </Link>
                  ))}
                </div>
              </div>

              <Link
                to="/live"
                className="flex cursor-pointer items-center justify-between rounded-lg bg-primary-500 px-5 py-4 text-background-50 transition-colors hover:bg-primary-600"
              >
                <span className="flex flex-col">
                  <span className="text-sm font-medium">蹲守吃瓜第一线</span>
                  <span className="mt-0.5 text-xs text-background-200">查看全部现场直击</span>
                </span>
                <i className="ri-arrow-right-line text-lg" />
              </Link>
            </div>
          </aside>
        </div>

        <section className="w-full bg-background-100">
          <div className="mx-auto max-w-[1240px] px-4 py-12 md:px-6 md:py-16">
            <div className="flex items-end justify-between">
              <h2 className="font-heading text-xl font-black text-foreground-950 md:text-2xl">
                你也许还想看
              </h2>
              <Link
                to="/live"
                className="cursor-pointer whitespace-nowrap text-sm text-foreground-500 transition-colors hover:text-primary-700"
              >
                更多内容
              </Link>
            </div>
            <div className="mt-7 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {articles
                .filter((item) => item.id !== article.id)
                .slice(0, 3)
                .map((item) => (
                  <ArticleCard key={item.id} article={item} />
                ))}
            </div>
          </div>
        </section>
      </article>
      <BackToTop />
    </main>
  );
}